import type { DateInput } from '@fullcalendar/core';
import type FullCalendar from '@fullcalendar/react';
import type { IDeviceCalendarProps } from 'labstep-web/components/Device/Calendar/types';
import { DeviceBookingModal } from 'labstep-web/components/DeviceBooking/Modal';
import { useHasAccessCreateMultiple } from 'labstep-web/components/Entity/Can/hooks';
import { EntityLink } from 'labstep-web/components/Entity/Link';
import { withActiveEditModal } from 'labstep-web/containers/ActiveEditModal';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import { EntityUpdateAnyContainer } from 'labstep-web/containers/Entity/Update/Any';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { FormShowEditDateToggleComponent } from 'labstep-web/core/Form/ShowEdit/Date/ToggleComponent';
import { Header } from 'labstep-web/core/Header';
import { Timeline } from 'labstep-web/core/Timeline';
import { SearchHOC } from 'labstep-web/hoc/Search';
import type { APIResponse } from 'labstep-web/models';
import { DeviceBooking } from 'labstep-web/models/device-booking.model';
import {
  dateOnly,
  format,
  formatDateOnly,
} from 'labstep-web/services/date.service';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

const DeviceCalendar: React.FC<IDeviceCalendarProps> = ({
  activeEditModal,
  devices,
  setActiveEditModal,
}) => {
  // Store only the date range in state
  const [dateRange, setDateRange] = React.useState({
    start: Date.now(),
    end: Date.now(),
    viewType: 'resourceTimelineDay',
  });
  const [isMounted, setIsMounted] = React.useState(false);
  const resourceLabelContent = (props) => {
    const device = props.resource.extendedProps.device;
    return <EntityLink entity={device} />;
  };

  const handleDatesSet = (info) => {
    setDateRange({
      start: info.start,
      end: info.end,
      viewType: info.view.type,
    });
  };

  const canCreateDeviceBooking = useHasAccessCreateMultiple(
    DeviceBooking.entityName,
    DeviceBooking.entityName,
    devices.map((device) => device.id),
  );
  const timeGridRef = React.createRef<FullCalendar>();

  useEffect(() => {
    // After the calendar renders, replace the title element
    if (timeGridRef?.current !== null) {
      // Replace the title element in the DOM
      const headerElement = document.querySelector(
        '.fc-toolbar-chunk:nth-child(1)',
      );
      if (headerElement !== null) {
        // Unmount the previous component
        ReactDOM.unmountComponentAtNode(headerElement);
        const dateComponent = (
          <FormShowEditDateToggleComponent
            onChange={(dates) => handleDateChange(dates)}
            viewComponent={
              <Header size="mini" noMargin>
                {dateOnly(formatDateOnly(dateRange.start))}
              </Header>
            }
            dateOnly
            canEdit
            defaultDate={format(dateRange.start)}
          />
        );
        // Clear existing title
        if (!isMounted) {
          headerElement.innerHTML = '';
          setIsMounted(true);
        }

        const fullcalendar = timeGridRef.current;

        const handleDateChange = (dates: DateInput[]) => {
          if (fullcalendar) {
            fullcalendar.getApi().gotoDate(dates[0]);
            setDateRange({
              start: (dates[0] as any).getTime(),
              end: dates[1]
                ? (dates[1] as any).getTime()
                : (dates[0] as any).getTime(),
              viewType: dateRange.viewType,
            });
          }
        };

        ReactDOM.render(dateComponent, headerElement);
      }
    }
  }, [dateRange]);

  return (
    <>
      <ParamsStateContextProvider value={{}}>
        <SearchHOC
          entityName={DeviceBooking.entityName}
          params={{
            device_id: devices.map((device) => device.id).join(','),
            count: 100,
            started_at_within: format(dateRange.start),
            ended_at_within: format(dateRange.end),
          }}
        >
          {({
            entities: deviceBookings,
          }: {
            entities: DeviceBooking[];
          }) => {
            const events = deviceBookings.map((deviceBooking) => ({
              id: deviceBooking.id.toString(),
              start: deviceBooking.started_at,
              end: deviceBooking.ended_at,
              extendedProps: { deviceBooking },
              resourceIds: [deviceBooking.device.id.toString()],
              title: deviceBooking.author
                ? deviceBooking.author.name
                : '',
            }));

            return (
              <EntityUpdateAnyContainer
                entityName={DeviceBooking.entityName}
              >
                {({ statuses: updateStatuses, update }) => (
                  <EntityCreateContainer
                    entityName={DeviceBooking.entityName}
                  >
                    {({ create, status }) => {
                      const isCreating = status && status.isFetching;
                      const isUpdating = updateStatuses.some(
                        (updateStatus) =>
                          updateStatus?.isFetching || false,
                      );
                      const isLoading = isCreating || isUpdating;
                      return (
                        <Timeline
                          ref={timeGridRef}
                          datesSet={handleDatesSet}
                          initialView="resourceTimelineDay"
                          slotLabelFormat={undefined}
                          headerToolbar={{
                            right: 'prev,next today',
                          }}
                          events={events}
                          isTimeline
                          resourceAreaHeaderContent="Devices"
                          resources={devices.map((device) => ({
                            id: device.id.toString(),
                            extendedProps: { device },
                          }))}
                          resourceLabelContent={resourceLabelContent}
                          eventStartEditable={false}
                          eventChange={(props) => {
                            props.revert();
                          }}
                          select={(event) => {
                            const resourceId = event.resource
                              ?.id as string;
                            if (
                              !canCreateDeviceBooking[
                                Number(resourceId)
                              ] ||
                              isLoading
                            ) {
                              return;
                            }
                            create(
                              {
                                started_at: format(event.start),
                                ended_at: format(event.end),
                                device_id: resourceId,
                              },
                              {
                                onSuccess: ({
                                  response,
                                }: {
                                  response: APIResponse;
                                }) =>
                                  setActiveEditModal({
                                    entityName:
                                      DeviceBooking.entityName,
                                    id: response.result,
                                  }),
                              },
                            );
                          }}
                          eventClick={({ event }) => {
                            setActiveEditModal({
                              entityName: DeviceBooking.entityName,
                              id: event.extendedProps.deviceBooking
                                .id,
                            });
                          }}
                          eventOverlap={false}
                          selectOverlap={false}
                          eventResizableFromStart={false}
                          eventDurationEditable={false}
                        />
                      );
                    }}
                  </EntityCreateContainer>
                )}
              </EntityUpdateAnyContainer>
            );
          }}
        </SearchHOC>
      </ParamsStateContextProvider>

      {
        // Need to separate out the modal in case time grid refreshes
        activeEditModal &&
          activeEditModal.entityName === DeviceBooking.entityName && (
            <DeviceBookingModal
              deviceBookingId={activeEditModal.id as number}
              onClose={() => setActiveEditModal(null)}
            />
          )
      }
    </>
  );
};

export default withActiveEditModal(DeviceCalendar);
